import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { FaHouse } from 'react-icons/fa6';
import './EmekidProtocolsIndex.scss';

// Icon imports
import allergyIcon from '../../assets/protocols/topics/allergy.svg';
import cardiologyIcon from '../../assets/protocols/topics/cardiology.svg';
import endoIcon from '../../assets/protocols/topics/endo.svg';
import gastroIcon from '../../assets/protocols/topics/gastro.svg';
import hematologyIcon from '../../assets/protocols/topics/hematology.svg';
import icuIcon from '../../assets/protocols/topics/icu.svg';
import infectiousIcon from '../../assets/protocols/topics/infectious.svg';
import metabolicIcon from '../../assets/protocols/topics/metabolic.svg';
import nephrologyIcon from '../../assets/protocols/topics/nephrology.svg';
import neurologyIcon from '../../assets/protocols/topics/neurology.svg';
import nicuIcon from '../../assets/protocols/topics/nicu.svg';
import oncologyIcon from '../../assets/protocols/topics/oncology.svg';
import psychiatryIcon from '../../assets/protocols/topics/psychiatry.svg';
import rheumatologyIcon from '../../assets/protocols/topics/rheumatology.svg';
import toolsIcon from '../../assets/protocols/topics/tools.svg';
import lungsIcon from '../../assets/protocols/topics/lungs.svg';
import poisoningIcon from '../../assets/protocols/topics/poisoning.svg';
import skinIcon from '../../assets/protocols/topics/skin.svg';
import erIcon from '../../assets/protocols/topics/er.svg';
import traumaIcon from '../../assets/protocols/topics/trauma.svg';

// Icon mapping by topic ID
const iconMap = {
  // Department topics
  'infectious': infectiousIcon,
  'hemato-gastro': hematologyIcon,
  'neuro-psych': neurologyIcon,
  'nephro-electrolytes': nephrologyIcon,
  'metabolic-endo': endoIcon,
  'allergy-immuno-resp': allergyIcon,
  'nicu-and-infants': nicuIcon,
  'picu': icuIcon,

  // Protocol type categories
  'nephrology': nephrologyIcon,
  'neurology': neurologyIcon,
  'gastro': gastroIcon,
  'psychiatry': psychiatryIcon,
  'metabolic': metabolicIcon,
  'endocrinology': endoIcon,
  'hematology': hematologyIcon,
  'immunology': allergyIcon,
  'rheumatology': rheumatologyIcon,
  'oncology': oncologyIcon,
  'cardiology': cardiologyIcon,
  'tools': toolsIcon,
  'misc-procedures': toolsIcon,
  'pulmonology': lungsIcon,
  'toxicology-env': poisoningIcon,
  'dermatology': skinIcon,
  'pediatric-er': erIcon,
  'trauma': traumaIcon,
};

// Color mapping by topic ID
const colorMap = {
  // Department topics
  'trauma': '#F57C00', // Orange - Emergency
  'infectious': '#8BC34A', // Lime Green - Infectious/Biological
  'hemato-gastro': '#C62828', // Deep Red - Blood/Hematology
  'neuro-psych': '#7E57C2', // Purple - Neurology
  'nephro-electrolytes': '#26A69A', // Teal - Metabolic/Scientific
  'metabolic-endo': '#26A69A', // Teal-Green - Genetic/Metabolic
  'allergy-immuno-resp': '#29B6F6', // Light Blue - Respiratory/Air
  'toxicology-env': '#FF6F00', // Dark Orange - Toxicology
  'misc-procedures': '#1E88E5', // Medical Blue - General
  'pediatric-er': '#F57C00', // Orange - Emergency
  'nicu-and-infants': '#81D4FA', // Light Blue - Pediatric
  'picu': '#546E7A', // Gray-Blue - Intensive Care/Surgical

  // Protocol type categories
  'nephrology': '#26A69A', // Teal
  'neurology': '#7E57C2', // Purple
  'pulmonology': '#29B6F6', // Light Blue
  'gastro': '#FF8A65', // Coral - Gastro
  'dermatology': '#FFA726', // Orange - Dermatology
  'psychiatry': '#9575CD', // Light Purple - Psychiatry
  'metabolic': '#26A69A', // Teal-Green
  'endocrinology': '#26A69A', // Teal
  'hematology': '#C62828', // Deep Red
  'immunology': '#29B6F6', // Light Blue (Allergy/Immuno/Resp)
  'rheumatology': '#8D6E63', // Brown - Rheumatology
  'oncology': '#5C6BC0', // Indigo - Oncology
  'cardiology': '#AD1457', // Burgundy/Dark Red
  'tools': '#78909C' // Gray - Tools
};

const categoriesByDepartment = [
  {
    id: 'pediatric-er',
    buttonName: 'מלר"ד ילדים',
    tags: ['מלר"ד ילדים'],
    topics: [
      { id: 'trauma', buttonName: 'טראומה', tags: ['טראומה'] },
      { id: 'infectious', buttonName: 'זיהומי', tags: ['זיהומי'] },
      { id: 'hemato-gastro', buttonName: 'המטולוגי / גסטרו / יילודים', tags: ['המטולוגי / גסטרו / יילודים'] },
      { id: 'neuro-psych', buttonName: 'נוירולוגי / פסיכיאטרי / סוציאלי', tags: ['נוירולוגי / פסיכיאטרי / סוציאלי'] },
      { id: 'nephro-electrolytes', buttonName: 'נפרולוגיה / אלקטרוליטים', tags: ['נפרולוגיה / אלקטרוליטים'] },
      { id: 'metabolic-endo', buttonName: 'מטאבולי / אנדו', tags: ['מטאבולי/ אנדו'] },
      { id: 'allergy-immuno-resp', buttonName: 'אלרגיה / אימונולוגיה / נשימתי', tags: ['אלרגיה / אימונולוגיה / נשימתי'] },
      { id: 'toxicology-env', buttonName: 'הרעלות / סביבתי', tags: ['הרעלות / סביבתי'] },
      { id: 'misc-procedures', buttonName: 'שונות / נהלי מלר"ד', tags: ['שונות / נהלי מלר"ד'] }
    ]
  },
  { id: 'nicu-and-infants', buttonName: 'פגיה וילודים', tags: ['פגיה וילודים'] },
  { id: 'picu', buttonName: 'טיפול נמרץ', tags: ['טיפול נמרץ'] }
];

const categoriesByProtocolType = [
  { id: 'infectious', buttonName: 'מחלות זיהומיות', tags: ['מחלות זיהומיות'] },
  { id: 'nephrology', buttonName: 'נפרולוגיה', tags: ['נפרולוגיה'] },
  { id: 'neurology', buttonName: 'נוירולוגיה', tags: ['נוירולוגיה'] },
  { id: 'pulmonology', buttonName: 'ריאות', tags: ['ריאות'] },
  { id: 'gastro', buttonName: 'גסטרואנטרולוגיה', tags: ['גסטרואנטרולוגיה'] },
  { id: 'dermatology', buttonName: 'עור', tags: ['עור'] },
  { id: 'psychiatry', buttonName: 'פסיכיאטריה', tags: ['פסיכיאטריה'] },
  { id: 'metabolic', buttonName: 'מחלות מטבוליות', tags: ['מחלות מטבוליות'] },
  { id: 'endocrinology', buttonName: 'אנדוקרינולוגיה', tags: ['אנדוקרינולוגיה'] },
  { id: 'hematology', buttonName: 'המטולוגיה', tags: ['המטולוגיה'] },
  { id: 'immunology', buttonName: 'אלרגיה / אימונולוגיה', tags: ['אלרגיה / אימונולוגיה'] },
  { id: 'rheumatology', buttonName: 'ראומטולוגיה', tags: ['ראומטולוגיה'] },
  { id: 'oncology', buttonName: 'אונקולוגיה', tags: ['אונקולוגיה'] },
  { id: 'cardiology', buttonName: 'קרדיולוגיה', tags: ['קרדיולוגיה'] },
  { id: 'tools', buttonName: 'כלי עזר', tags: ['כלי עזר'] }
];

const EmekidProtocolsIndex = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  useEffect(() => {
    const deptId = searchParams.get('dept');
    if (deptId) {
      const department = categoriesByDepartment.find(d => d.id === deptId);
      if (department) {
        setSelectedDepartment(department);
      }
    } else {
      setSelectedDepartment(null);
    }
  }, [searchParams]);

  const handleDepartmentClick = (department) => {
    const currentParams = new URLSearchParams(window.location.search);
    if (!department.topics || department.topics.length === 0) {
      currentParams.set('tags', department.tags.join(','));
    } else {
      currentParams.set('dept', department.id);
    }
    navigate(`${window.location.pathname}?${currentParams.toString()}`);
  };

  const handleBackClick = () => {
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.delete('dept');
    navigate(`${window.location.pathname}?${currentParams.toString()}`);
  };

  const handleCategoryClick = () => {
    // Let the Link component handle navigation
    // Then scroll the main content container to top after 100ms
    setTimeout(() => {
      const mainContent = document.getElementById('main-content');
      if (mainContent) {
        mainContent.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const renderCategoryLink = (category, departmentTags = null) => {
    const allTags = departmentTags
      ? [...departmentTags, ...category.tags]
      : category.tags;
    const tags = allTags.join(',');
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('tags', tags);
    const to = `${window.location.pathname}?${currentParams.toString()}`;
    const icon = iconMap[category.id];
    const color = colorMap[category.id];
    return (
      <Link
        to={to}
        key={category.id}
        className="category-link"
        style={{
          textDecoration: 'none',
          borderRight: color ? `5px solid ${color}` : 'none'
        }}
        onClick={handleCategoryClick}
      >
        {icon && <img src={icon} alt="" className="category-icon" />}
        <span>{category.buttonName}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
      </Link>
    );
  };

  const renderDepartmentButton = (department) => {
    const icon = iconMap[department.id];
    const color = colorMap[department.id];
    // Always render as a button for departments, even if they don't have sub-topics
    // If no sub-topics, clicking will just show the category link directly
    return (
      <button
        key={department.id}
        className="category-link department-button"
        onClick={() => handleDepartmentClick(department)}
        style={{
          borderRight: color ? `5px solid ${color}` : 'none'
        }}
      >
        {icon && <img src={icon} alt="" className="category-icon" />}
        <span>{department.buttonName}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
      </button>
    );
  };

  const renderBreadcrumb = () => {
    const ChevronSeparator = () => (
      <span className="breadcrumb-separator">
        <svg width="9" height="16" viewBox="0 0 9 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.5 14.5L1.5 8L7.5 1.5" stroke="#9CACBA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );

    if (!selectedDepartment) {
      return (
        <div className="breadcrumb">
          <span className="breadcrumb-current"><FaHouse /></span>
        </div>
      );
    }
    return (
      <div className="breadcrumb">
        <span className="breadcrumb-link" onClick={handleBackClick}><FaHouse /></span>
        <ChevronSeparator />
        <span className="breadcrumb-current">{selectedDepartment.buttonName}</span>
      </div>
    );
  };

  return (
    <div className="emekid-protocols-index">
      <h1 className="emekid-main-title">ברוכים הבאים לעמקיד</h1>
      {renderBreadcrumb()}

      {!selectedDepartment ? (
        <>
          <div className="category-section">
            <h2 className="category-title">איפה אני?</h2>
            <div className="category-grid departments-grid">
              {categoriesByDepartment.map(department => renderDepartmentButton(department))}
            </div>
          </div>

          <div className="category-section">
            <h2 className="category-title">באיזה נושא?</h2>
            <div className="category-grid protocol-types-grid">
              {categoriesByProtocolType.map(category => renderCategoryLink(category))}
            </div>
          </div>
        </>
      ) : (
        <div className="category-section">
          <h2 className="category-title">{selectedDepartment.buttonName} - בחר נושא</h2>
          <div className="category-grid topics-grid">
            {selectedDepartment.topics && selectedDepartment.topics.length > 0 ? (
              selectedDepartment.topics.map(topic => renderCategoryLink(topic, selectedDepartment.tags))
            ) : (
              renderCategoryLink(selectedDepartment)
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmekidProtocolsIndex;
